// src/lib/logger.ts

/**
 * Checks if the current environment is staging.
 * Relies on the NEXT_PUBLIC_APP_ENV environment variable being set to 'staging' in the deployment environment.
 */
const isStaging = process.env.NEXT_PUBLIC_APP_ENV === 'staging';

/**
 * Checks if the current environment is production.
 * Relies on the NEXT_PUBLIC_APP_ENV environment variable being set to 'production' in the deployment environment.
 */
const isProduction = process.env.NEXT_PUBLIC_APP_ENV === 'production';

/**
 * Logs informational messages. Only outputs in the staging environment.
 * @param message - The primary message to log.
 * @param optionalParams - Additional data to log.
 */
export const info = (message?: any, ...optionalParams: any[]): void => {
  if (isStaging) {
    console.info(`[INFO] ${message}`, ...optionalParams);
  }
};

/**
 * Logs debug messages. Only outputs in the staging environment.
 * Useful for detailed debugging information that shouldn't be in production.
 * @param message - The primary message to log.
 * @param optionalParams - Additional data to log.
 */
export const debug = (message?: any, ...optionalParams: any[]): void => {
  if (isStaging) {
    // Using console.debug might be filtered by browser/Node settings,
    // console.log might be more reliable for visibility during staging.
    console.log(`[DEBUG] ${message}`, ...optionalParams);
  }
};

/**
 * Logs warning messages. Outputs in both staging and production environments.
 * Useful for non-critical issues that should still be noted.
 * @param message - The primary message to log.
 * @param optionalParams - Additional data to log.
 */
export const warn = (message?: any, ...optionalParams: any[]): void => {
  if (isStaging || isProduction) {
    console.warn(`[WARN] ${message}`, ...optionalParams);
  }
};

/**
 * Logs error messages. Outputs in both staging and production environments.
 * Used for critical errors that need attention.
 * @param message - The primary message to log (can be an Error object).
 * @param optionalParams - Additional data or error context.
 */
export const error = (message?: any, ...optionalParams: any[]): void => {
  if (isStaging || isProduction) {
    console.error(`[ERROR] ${message}`, ...optionalParams);
  }
};

// Optional: A general log function that behaves like 'info'
export const log = info;

const logger = {
  info,
  debug,
  warn,
  error,
  log,
};

export default logger;
